import { ReactElement } from "react";

import { FILLED_COLUMN, ParsedReport } from "../transaction-report.columns";

interface ReportPreviewProps {
  parsed: ParsedReport;
  /** How many rows to render — the rest are summarised in the footer. */
  limit?: number;
}

const normalise = (value: string) =>
  value.trim().toUpperCase().replace(/\s+/g, "_");

/**
 * A read-only look at what was picked: how many rows and columns, and a
 * sample of the sheet. Nothing here judges the file — it is shown so the
 * operator can confirm they grabbed the right export before sending it.
 */
const ReportPreview: React.FC<ReportPreviewProps> = ({
  parsed,
  limit = 8,
}): ReactElement => {
  const { headers, rows } = parsed;

  return (
    <>
      <div className="stats">
        <div className="stat">
          <label>Rows</label>
          <strong>{rows.length.toLocaleString("en-US")}</strong>
        </div>
        <div className="stat">
          <label>Columns</label>
          <strong>{headers.length}</strong>
        </div>
      </div>

      {rows.length > 0 && (
        <>
          <div className="preview-wrap">
            <table>
              <thead>
                <tr>
                  {headers.map((header) => (
                    <th
                      key={header}
                      className={
                        normalise(header) === FILLED_COLUMN ? "is-filled" : ""
                      }
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, limit).map((row, index) => (
                  <tr key={index}>
                    {headers.map((header) => {
                      const value = String(row[header] ?? "").trim();
                      const isIp = normalise(header) === FILLED_COLUMN;
                      return (
                        <td
                          key={header}
                          className={`${isIp ? "is-filled" : ""}${
                            !value ? " is-blank" : ""
                          }`}
                          title={value}
                        >
                          {value || "—"}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length > limit && (
            <p className="preview-foot">
              Showing {limit} of {rows.length.toLocaleString("en-US")} rows.
            </p>
          )}
        </>
      )}
    </>
  );
};

export default ReportPreview;
